import type {ElcFindingsData} from "../../types";

export const ELC_FINDINGS: ElcFindingsData[] = [
  {
    elcId: "elc-control-environment",
    findings: [
      {
        id: "find-ce-01",
        rating: "low",
        internal: {
          title: "Audit committee meeting minutes lack documented follow-up actions",
          content:
            "Review of audit committee meeting minutes for Q3 and Q4 2025 identified that **3 out of 6 meetings** did not include a documented action item register or follow-up tracker. While discussion topics were recorded, specific action items, owners, and due dates were not consistently captured.\n\n**Root cause:** The meeting minutes template does not include a structured action item section.\n\n**Impact:** Without documented follow-up actions, there is limited evidence that governance decisions are being tracked to completion, reducing the effectiveness of board oversight.",
        },
        clientFacing: null,
        controlId: "ctrl-ce-03",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
      {
        id: "find-ce-02",
        rating: "low",
        internal: {
          title: "Ethics training completion rates below target",
          content:
            "The annual ethics training programme achieved a **94% completion rate** against the entity's 100% target. The remaining 6% comprises employees on extended leave. While the shortfall is explained by legitimate absences, **no catch-up mechanism** exists to ensure training is completed upon return.\n\n**Root cause:** The training management system does not automatically re-enrol employees returning from extended leave.\n\n**Impact:** Employees returning from leave may not receive updated ethics training, creating a potential gap in the control environment for the period between return and the next annual training cycle.",
        },
        clientFacing: null,
        controlId: "ctrl-ce-02",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
    ],
  },
  {
    elcId: "elc-monitoring-governance",
    findings: [
      {
        id: "find-moc-01",
        rating: "moderate",
        internal: {
          title: "Deficiency remediation SLAs not consistently met",
          content:
            "Testing of the deficiency remediation tracker identified that **4 out of 12 moderate-severity deficiencies** exceeded the 60-day SLA, with two items open for over 120 days.\n\n**Root cause:** Remediation owners are not receiving automated reminders and the monthly management review does not consistently escalate overdue items. The spreadsheet-based tracker lacks workflow automation.\n\n**Impact:** Persistent control weaknesses increase the likelihood that deficiencies remain unaddressed, potentially leading to material misstatement or regulatory findings.",
        },
        clientFacing: {
          title: "Timeliness of deficiency remediation",
          content:
            "We identified instances where deficiency remediation exceeded established SLA targets. We recommend implementing an automated tracking tool with escalation workflows to ensure timely remediation and management visibility.",
        },
        controlId: "ctrl-moc-03",
        includeInternal: true,
        includeClientFacing: true,
        status: "in-review",
      },
      {
        id: "find-moc-02",
        rating: "low",
        internal: {
          title: "Automated exception monitoring rules not reviewed or updated in 12+ months",
          content:
            "The automated exception monitoring rules configured in SAP S/4HANA were last reviewed and updated in **January 2025**, over 12 months ago. During this period, **3 new transaction types** were onboarded and **2 business process changes** were implemented without corresponding updates to the exception rule set.\n\n**Root cause:** There is no formal periodic review process for exception monitoring configurations. Updates are performed on an ad hoc basis when issues are identified.\n\n**Impact:** The exception monitoring system may not detect anomalous transactions related to new or changed business processes, reducing the effectiveness of automated detective controls.",
        },
        clientFacing: null,
        controlId: "ctrl-moc-02",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
    ],
  },
  {
    elcId: "elc-risk-assessment",
    findings: [
      {
        id: "find-ra-01",
        rating: "moderate",
        internal: {
          title: "Risk register not updated to reflect recent significant business change",
          content:
            "The entity's enterprise risk register was not updated to reflect the loss of the second-largest customer (contract not renewed, December 2025). Despite the revenue impact being acknowledged informally, **no formal risk assessment** was performed to evaluate the financial reporting implications, including potential impairment triggers for trade receivables, going concern considerations, and revenue forecast adjustments.\n\n**Root cause:** The change impact assessment process is not consistently triggered for external market events. The current process relies on business unit escalation, and the commercial team did not initiate a formal assessment.\n\n**Impact:** Without a formal assessment, the entity may not have identified all financial reporting risks arising from the customer loss, increasing the likelihood of incomplete or inaccurate disclosures.",
        },
        clientFacing: {
          title: "Timeliness of risk register updates for significant changes",
          content:
            "We identified an instance where a significant business change was not reflected in the entity's risk register or subject to formal financial reporting impact assessment. We recommend formalising trigger criteria for mandatory change impact assessments and ensuring all significant changes are evaluated within an agreed timeframe.",
        },
        controlId: "ctrl-ra-03",
        includeInternal: true,
        includeClientFacing: true,
        status: "in-progress",
      },
      {
        id: "find-ra-02",
        rating: "low",
        internal: {
          title: "Risk assessment methodology not formally reviewed in 18+ months",
          content:
            "The entity's risk assessment methodology (qualitative likelihood/impact matrix) has not been formally reviewed or updated since **July 2024**. During this period, the organisation has undergone significant changes including an acquisition, system migration, and new regulatory requirements. The methodology continues to use the same risk categories and scoring criteria without recalibration.\n\n**Root cause:** There is no formal periodic review cycle for the risk assessment methodology. Updates are performed on an ad hoc basis.\n\n**Impact:** An outdated methodology may not adequately capture emerging risk categories or reflect changes in the entity's risk profile, potentially leading to under-assessment of material risks.",
        },
        clientFacing: null,
        controlId: "ctrl-ra-01",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
    ],
  },
  {
    elcId: "elc-it-environment",
    findings: [
      {
        id: "find-gitc-01",
        rating: "high",
        internal: {
          title: "Untimely deprovisioning of terminated user accounts",
          content:
            "Testing of user access deprovisioning identified that **5 out of 20 sampled terminations** had access removed more than **7 business days** after the termination effective date. In two cases, access remained active for over 30 days.\n\n**Root cause:** The HR termination notification process relies on a manual email to IT, and there is no automated integration between the HRIS system and identity management platform. Additionally, there is no periodic reconciliation between HR records and active user accounts.\n\n**Impact:** Terminated employees may retain access to financially significant applications, creating a risk of unauthorized transactions or data exfiltration during the gap period.",
        },
        clientFacing: {
          title: "Timeliness of user access deprovisioning",
          content:
            "We identified delays in the removal of system access for terminated employees. We recommend implementing an automated integration between the HR information system and identity management platform to ensure timely deprovisioning upon termination, supplemented by a weekly reconciliation of active accounts against HR records.",
        },
        controlId: "ctrl-gitc-01a",
        includeInternal: true,
        includeClientFacing: true,
        status: "in-progress",
      },
      {
        id: "find-gitc-02",
        rating: "low",
        internal: {
          title: "Change advisory board meeting minutes lack documented risk assessment",
          content:
            "While changes are presented to the CAB for approval, the meeting minutes **do not consistently document the risk assessment** performed for each change. In **3 out of 15 sampled CAB meetings**, the minutes recorded the approval decision but lacked documentation of the risk classification, testing requirements, or rollback plan review.\n\n**Root cause:** The CAB meeting template does not include mandatory fields for risk assessment documentation.\n\n**Impact:** Without documented evidence of risk assessment, there is limited audit trail to demonstrate that changes were evaluated for potential impact on financial reporting systems prior to approval.",
        },
        clientFacing: null,
        controlId: "ctrl-gitc-02a",
        includeInternal: true,
        includeClientFacing: false,
        status: "complete",
      },
    ],
  },
];
